package com.milta.feature.optimizationquota.presentation

import com.milta.feature.optimizationquota.domain.entity.OptimizationQuota
import com.milta.feature.optimizationquota.domain.usecase.GetMyOptimizationQuotaUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * MIL-97 — the caller's own optimization counter, shared by every screen that shows an
 * optimization button (create route, batch preview, route in progress, web import) and by
 * "Mes optimisations". Koin `single`: each screen calls [refresh] when it opens and after each
 * optimization action, so a value spent from another screen or device is picked up.
 *
 * `null` until the first successful read: the buttons then show no counter and stay enabled
 * (fail-open, the server still enforces the limit and answers with the quota error).
 */
class OptimizationQuotaStore(private val getMyQuota: GetMyOptimizationQuotaUseCase) {

    private val _quota = MutableStateFlow<OptimizationQuota?>(null)
    val quota: StateFlow<OptimizationQuota?> = _quota.asStateFlow()

    /** Bumped by [clear]: a read started before a logout never writes the previous account's value. */
    private val generation = MutableStateFlow(0)

    /** A failed read keeps the last known value. */
    suspend fun refresh(): Result<OptimizationQuota> {
        val started = generation.value
        return getMyQuota().onSuccess { quota ->
            if (generation.value == started) _quota.value = quota
        }
    }

    /** Logout / account switch — never show the previous account's counter. */
    fun clear() {
        generation.value += 1
        _quota.value = null
    }
}
